// Three-pane workspace (C): the live Summary pane. Reuses the engine result (no engine change) to show the
// net-spendable-income shares, the FC 4061(b) add-on division and the bonus % range — the same figures as the
// Summary report, in the right-pane panel style. The bonus range is deferred so it never blocks typing.
import type { PartyIndex } from '@divorceos/schema';
import { bonusTable } from '@divorceos/engine';
import type { CaseView } from './caseView';
import { ENGINE_OPTS, money } from './model';
import { useDeferred } from './reports/common';

export function SummaryPane({ v }: { v: CaseView }) {
  const { c, result: r, labels: L } = v;
  const payor = r?.guideline.total.payor ?? null;
  const bonusJob = useDeferred(
    () => (payor === null || !r ? null : bonusTable(v.calc, payor, true, false, ENGINE_OPTS)),
    `${v.doc.calcAt}|summarypane|${payor}`,
  );

  if (!r) return <div className="ph muted" style={{ padding: 12 }}>No result to summarize yet.</div>;

  const g = r.guideline;
  const netD: [number, number] = [r.parties[0].netDisposable, r.parties[1].netDisposable];
  const sumND = netD[0] + netD[1];
  const split = (i: PartyIndex) => (sumND > 0 ? netD[i] / sumND : 0.5);
  const pct = (x: number) => `${(x * 100).toFixed(1)}%`;

  const spend: [number, number] = [r.parties[0].guideline.netSpendable, r.parties[1].guideline.netSpendable];
  const shareSpend = (i: PartyIndex) => r.parties[i].guideline.percentOfCombinedSpendable;

  const bonusRows = (bonusJob.value?.rows ?? []).filter((x) => x.amount > 0);
  const combined = bonusRows.map((x) => ((x.additionalCs + x.additionalSs) / x.amount) * 100);
  const bLo = combined.length ? Math.min(...combined) : null;
  const bHi = combined.length ? Math.max(...combined) : null;

  return (
    <>
      <section className="panel">
        <header><h3>Net spendable income</h3></header>
        <table className="lines"><tbody>
          <tr><td>{L[0]}</td><td className="py">{pct(shareSpend(0))}</td><td className="n">{money(c, spend[0])}</td></tr>
          <tr><td>{L[1]}</td><td className="py">{pct(shareSpend(1))}</td><td className="n">{money(c, spend[1])}</td></tr>
          <tr className="tot"><td>Combined</td><td className="py" /><td className="n">{money(c, spend[0] + spend[1])}</td></tr>
        </tbody></table>
        <div className="foot">Share of the combined net spendable income after support.</div>
      </section>

      <section className="panel">
        <header><h3>Add-on division</h3><span className="sub">FC 4061(b)</span></header>
        <table className="lines"><tbody>
          <tr><td>{L[0]}</td><td className="py" /><td className="n">{pct(split(0))}</td></tr>
          <tr><td>{L[1]}</td><td className="py" /><td className="n">{pct(split(1))}</td></tr>
          <tr className="tot"><td>Add-ons</td><td className="py" /><td className="n">{money(c, g.addOns.amount)}</td></tr>
        </tbody></table>
        <div className="foot">Divided in proportion to each parent's net disposable income.</div>
      </section>

      <section className="panel">
        <header><h3>Support above base pay</h3>{bonusJob.busy && <span className="sub">computing…</span>}</header>
        <table className="lines"><tbody>
          {payor === null ? (
            <tr><td>No payor of support</td><td className="py" /><td className="n">—</td></tr>
          ) : (
            <>
              <tr><td>{L[payor]} pays</td><td className="py">of income above base</td><td className="n">{bLo !== null ? `${bLo.toFixed(0)}–${bHi!.toFixed(0)}%` : '—'}</td></tr>
              <tr><td>See bonus attachment</td><td className="py" colSpan={2} style={{ textAlign: 'right' }}>full schedule</td></tr>
            </>
          )}
        </tbody></table>
      </section>
    </>
  );
}
